import { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { ChipButton } from '@/components/ChipButton';
import { SizedBox as SizeBoxComponent } from '@/components/SizedBox';
import { useHasPublishedMarkets } from '@/feature/event-details-sbk/hooks/use-market-cache';
import { useMarketName } from '@/feature/event-details-sbk/hooks/use-market-name';
import { MarketCategory } from '@/feature/event-details-sbk/types';
import { Event } from '@/feature/event-details-sbk/types';

const SizedBox = () => <SizeBoxComponent value={8} />;

type PlayerPropFilterButtonProps = {
    marketCategory: MarketCategory;
    sportName: string;
    selectedCategory: MarketCategory;
    updateSelectedCategory: (category: MarketCategory) => void;
};

const PlayerPropFilterButton = ({
    marketCategory,
    sportName,
    selectedCategory,
    updateSelectedCategory,
}: PlayerPropFilterButtonProps) => {
    const marketName = useMarketName({
        sportName,
        marketType: marketCategory.market_type,
        fallback: marketCategory.market_type.code,
    });

    const publishedMarkets = useHasPublishedMarkets([marketCategory]);

    if (!publishedMarkets) {
        return null;
    }

    const active = selectedCategory.market_type.code === marketCategory.market_type.code;

    return (
        <ChipButton
            key={marketCategory.market_type.code}
            label={marketName}
            active={active}
            onPress={() => updateSelectedCategory(marketCategory)}
        />
    );
};

type PlayerPropsFiltersProps = {
    updateSelectedCategory: (category: MarketCategory) => void;
    selectedCategory: MarketCategory;
    marketCategories: MarketCategory[];
    event: Event;
};

export const PlayerPropsFilters = ({
    updateSelectedCategory,
    selectedCategory,
    marketCategories,
    event,
}: PlayerPropsFiltersProps) => {
    const sportName = event.sport.name;

    const renderItem = useCallback(
        ({ item }: { item: MarketCategory }) => (
            <PlayerPropFilterButton
                marketCategory={item}
                sportName={sportName}
                selectedCategory={selectedCategory}
                updateSelectedCategory={updateSelectedCategory}
            />
        ),
        [sportName, selectedCategory, updateSelectedCategory]
    );

    return (
        <FlatList
            horizontal
            data={marketCategories}
            ListFooterComponent={<SizeBoxComponent value={16} />}
            ListHeaderComponent={<SizeBoxComponent value={16} />}
            ItemSeparatorComponent={SizedBox}
            contentContainerStyle={styles.flatListStyle}
            showsHorizontalScrollIndicator={false}
            renderItem={renderItem}
        />
    );
};

const styles = StyleSheet.create({
    flatListStyle: {
        paddingVertical: 16,
        minWidth: '100%',
    },
});
